import { setConsumerId } from "@carsdotcom/ccdp-sdk";
import styled from "@emotion/styled";
import React, { Component, ReactNode } from "react";
import PageVisibility from "react-page-visibility";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { analyticsFEA } from "src/AnalyticsFEA";
import { isPolling, start, stop as stopPolling } from "src/services/poller";
import { setSignIn } from "src/store/actions/onlineShopper/signIn";
import { openSubModal } from "src/store/actions/onlineShopper/subModal";
import { toggleModal } from "src/store/actions/openModal";
import { pollingSuccess as pollingSuccessAction } from "src/store/actions/poller";
import { EXPAND_ALL } from "src/store/constants/deal";
import { getFetchOptionsFromStore } from "src/store/dataService";
import { buildHeader } from "src/store/dataService/routes/shared/osfetch";
import { getVehiclesArrayForFEA } from "src/store/selectors/vehicles";
import { Deal } from "Types/api/Deal";
import { ApplicationStore } from "Types/state";
import Modal from "../Modal";

export interface Props extends ReturnType<typeof mapStateToProps> {
    refreshDeal: any;
    toggleModal: () => void;
    pollingSuccess: (deal: Deal) => void;
    children: ReactNode;
}

interface State {
    isPageVisible: boolean;
}

const ModalWrapper = styled(Modal)`
    z-index: 1220301;
`;

/**
 * Top level "wrapping" modal for Online Shopper.
 * Anything passed into it as prop 'children' will be rendered.
 * In real use, we should only care about closing the modal.
 * Opening the modal will be called from the injected site.
 */
class OSModal extends Component<Props & RouteComponentProps, State> {
    public state: Readonly<State> = {
        isPageVisible: true,
    };

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
        const currentRevision = this.props.deal.current_data_revision || 0;
        const previousRevision = prevProps.deal.current_data_revision || 0;

        // stop polling if: user switches to another browser tab || the app modal is closed || browsing in app inventory
        if (
            (!this.state.isPageVisible || !this.props.open || this.props.history.location.pathname === "/inventory") &&
            !this.isRevisionDecimal(currentRevision)
        ) {
            stopPolling();
            return;
        }

        if (!isPolling() && !this.isRevisionDecimal(currentRevision)) {
            this.startPolling();
        }

        // stop existing polling and start polling right away if the revision is incremented by the app instead of api
        if (this.isRevisionDecimal(currentRevision) && currentRevision !== previousRevision) {
            this.restartPolling();
        }

        if (this.props.ccdpId && this.props.ccdpId !== prevProps.ccdpId) {
            this.setConsumerIdInSdk(this.props.ccdpId);
        }
    }

    public componentWillUnmount() {
        stopPolling();
    }

    public closeModal = () => {
        this.props.toggleModal();
        if (this.props.deal.uuid) {
            analyticsFEA.engine.closed(this.props.deal.uuid, this.props.vehiclesForFEA);
        }
    };

    public render() {
        return (
            <div>
                {this.renderModal()}

                <PageVisibility onChange={this.handleVisibility} />
            </div>
        );
    }

    protected renderModal = () => {
        return (
            <ModalWrapper
                // hack to find the dialog to scroll
                id="osModal"
                closeModal={this.closeModal}
                maxWidth={this.props.modalSize === "lg" ? "1350px" : "720px"}
                open={this.props.open}
            >
                {this.props.children}
            </ModalWrapper>
        );
    };

    protected handleVisibility = (isPageVisible: boolean) => {
        this.setState({ isPageVisible });
    };

    protected pollingHandleSuccess = async (deal: Deal, eTag: string) => {
        this.restartPolling(eTag);

        if (deal.current_data_revision !== (this.props.deal.current_data_revision || 0)) {
            const { pollingSuccess } = this.props;
            pollingSuccess(deal);
        }
    };

    protected pollingHandleFailure = (statusCode: number) => {
        this.props.refreshDeal(statusCode);
    };

    private async setConsumerIdInSdk(ccdpId: string) {
        await setConsumerId(ccdpId);
    }

    private restartPolling = (eTag?: string) => {
        stopPolling();
        this.startPolling(eTag);
    };

    private startPolling = (eTag?: string) => {
        if (this.props.deal.uuid) {
            const { url, referer } = getFetchOptionsFromStore();
            const headers = buildHeader(referer);

            // tslint:disable-next-line: no-floating-promises
            start(
                {
                    eTag,
                    headers,
                    url: `${url}/deals/${this.props.deal.uuid}?${EXPAND_ALL}`,
                },
                this.pollingHandleSuccess,
                this.pollingHandleFailure
            );
        }
    };

    /**
     * Returns a non-zero number if the revision is a decimal
     * @param revision
     */
    private isRevisionDecimal = (revision: number): number => {
        return revision % 1;
    };
}

function mapStateToProps(state: ApplicationStore) {
    const { deal, onlineShopper, tracking } = state;
    return {
        deal,
        modalSize: onlineShopper.modalSize,
        open: onlineShopper.isAppOpen,
        vehiclesForFEA: getVehiclesArrayForFEA(state),
        ccdpId: tracking.ccdp?.consumer_id,
    };
}

const mapDispatchToProps = (dispatch: any) => ({
    refreshDeal: (statusCode: number) => {
        dispatch(openSubModal({ modalType: "badDealApologiesModal", statusCode }));
    },
    toggleModal: () => {
        dispatch(toggleModal());
        dispatch(setSignIn({ routeSignIn: false }));
    },
    pollingSuccess: (deal: Deal) => dispatch(pollingSuccessAction(deal)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(OSModal));
